/* eslint-disable react/prop-types */
import {useContext, useState} from 'react'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomSpinner from '@s-ui/react-atom-spinner'
import Context from '@s-ui/react-context'
import {useToggle} from '@s-ui/react-hooks'
import MoleculeCollapsible from '@s-ui/react-molecule-collapsible'
import DocumentContentful from '@adv-mt/mt-document-contentful'
import ListFeatures from '@adv-mt/mt-list-features'
import MediaDimensions from '@adv-mt/mt-media-dimensions'
import ModalDisclaimer, {ModalDisclaimerIcon} from '@adv-mt/mt-modal-disclaimer'
import PanelRating, {panelRatingLayouts} from '@adv-mt/mt-panel-rating'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

import {documentContentfulOptions} from '../../ModelDetail/components/contentful-options.js'
import {BASE_CLASS as PARENT_BASE_CLASS, collapsibleMinHeight} from '../settings.js'
const RESULT_BASE_CLASS = `${PARENT_BASE_CLASS}Results`
const BASE_CLASS = `${RESULT_BASE_CLASS}Overview`
const noop = () => {}

export const VehicleOverview = ({info = null, loading = false, onCtaClick = noop}) => {
  const {i18n} = useContext(Context)

  const [modalDisclaimerTitle, setModalDisclaimerTitle] = useState('')
  const [isAiDisclaimerOpen, toggleAiDisclaimer] = useToggle(false)

  const toggleModalDisclaimer = title => {
    title && setModalDisclaimerTitle(title)
    toggleAiDisclaimer()
  }

  if (loading) {
    return (
      <div className={`${BASE_CLASS}-loading`}>
        <AtomSpinner />
      </div>
    )
  }

  if (!info) {
    return <p>No se pudo cargar el detalle del coche.</p>
  }

  const {body, cons, dimensionsImage, height, make, model, pros, rating, length, width} = info

  const hasProsAndCons = Boolean(pros.length && cons.length)
  const hasRating = Boolean(rating.length)

  const labelReview = i18n.t('COMMON.REVIEW')
  const labelReviewMoreInfo = i18n.t('COMMON_MORE_INFO_ABOUT', {info: labelReview.toLowerCase()})
  const labelProsAndCons = i18n.t('NEWS.DETAIL_SECTION_PROS_AND_CONS', {
    smart_count: 1
  })
  const labelProsAndConsMoreInfo = i18n.t('COMMON_MORE_INFO_ABOUT', {
    info: labelProsAndCons.toLowerCase()
  })
  const labelRating = i18n.t('COMMON.EXPERT_RATING')
  const labelRatingMoreInfo = i18n.t('COMMON_MORE_INFO_ABOUT', {
    info: labelRating.toLowerCase()
  })

  const reportAiLink = i18n.t('COMMON.REPORT_AI_CONTENT_LINK', {
    url: i18n.t('COMMON.COCHES_URL') + window.location.pathname
  })

  return (
    <aside className={`${BASE_CLASS}`}>
      <section className={`${BASE_CLASS}-section`}>
        <MoleculeCollapsible
          alignButtonText="left"
          showText={i18n.t('MODEL_DETAIL.HEADER.READ_MORE')}
          hideText={i18n.t('MODEL_DETAIL.HEADER.READ_LESS')}
          height={collapsibleMinHeight}
          icon={<IconChevronDown />}
        >
          <ModalDisclaimerIcon
            onClick={() => toggleModalDisclaimer(i18n.t('COMMON_INFO_ABOUT', {info: labelReview.toLowerCase()}))}
            label={labelReviewMoreInfo}
          >
            <TitleBasic as={titleBasicTags.h2} size={titleBasicSizes.xs} title={labelReview} />
          </ModalDisclaimerIcon>
          <div className={`${BASE_CLASS}-descriptionContent`}>
            <DocumentContentful content={body} rendererOptions={documentContentfulOptions} />
          </div>
        </MoleculeCollapsible>
      </section>
      <section className={`${BASE_CLASS}-section`}>
        <TitleBasic
          as={titleBasicTags.h2}
          size={titleBasicSizes.xs}
          title={i18n.t('MODEL_SIZES.TITLE', {make, model})}
        />
        <MediaDimensions width={width} height={height} length={length} imageSrc={dimensionsImage} />
      </section>
      {hasProsAndCons && (
        <section className={`${BASE_CLASS}-section`}>
          <div className={`${BASE_CLASS}-sectionTitle`}>
            <ModalDisclaimerIcon
              onClick={() => toggleModalDisclaimer(i18n.t('COMMON_INFO_ABOUT', {info: labelProsAndCons.toLowerCase()}))}
              label={labelProsAndConsMoreInfo}
            >
              <TitleBasic as={titleBasicTags.h2} title={labelProsAndCons} size={titleBasicSizes.xs} />
            </ModalDisclaimerIcon>
          </div>
          <ListFeatures likes={pros} dislikes={cons} />
        </section>
      )}
      {hasRating && (
        <section className={`${BASE_CLASS}-section`}>
          <div className={`${BASE_CLASS}-sectionTitle`}>
            <ModalDisclaimerIcon
              onClick={() => toggleModalDisclaimer(i18n.t('COMMON_INFO_ABOUT', {info: labelRating.toLowerCase()}))}
              label={labelRatingMoreInfo}
            >
              <TitleBasic as={titleBasicTags.h2} title={labelRating} size={titleBasicSizes.xs} />
            </ModalDisclaimerIcon>
          </div>
          <PanelRating rating={rating} layout={panelRatingLayouts.VERTICAL} />
        </section>
      )}
      <section className={`${BASE_CLASS}-section ${BASE_CLASS}-cta`}>
        <AtomButton
          fullWidth
          color={atomButtonColors.PRIMARY}
          design={atomButtonDesigns.SOLID}
          onClick={onCtaClick}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.MEDIUM}
        >
          {i18n.t('COMMON.CONFIGURE')}
        </AtomButton>
      </section>
      <ModalDisclaimer
        title={modalDisclaimerTitle}
        text={i18n.t('COMMON.AI_DISCLAIMER', {
          termsAndConditionsLinkStart: `<a href="${i18n.t(
            'COMMON.TERMS_AND_CONDITIONS_SECTION_LINK.AI_GENERATED_CONTENT'
          )}" class='mt-ModalDisclaimer-link' target="_blank">`,
          termsAndConditionsLinkEnd: '</a>',
          reportAiLinkStart: `<a href="${reportAiLink}" class='mt-ModalDisclaimer-link' target="_blank">`,
          reportAiLinkEnd: '</a>'
        })}
        open={isAiDisclaimerOpen}
        onClose={toggleModalDisclaimer}
      />
    </aside>
  )
}
